import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import CashBookPrint from "./CashBookPrint";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";
import price from "../../helpers/calculatePrice";
import roundOffTotal from "../../helpers/roundOffTotal";

/**
 * Build a flat ESC/POS receipt object from the bill orderData for the Android
 * native thermal printer bridge. Mirrors the same calculation logic as
 * BillComponent/ProductTable so the printed receipt matches the screen.
 *
 * jobId format: "bill:<branchOrderNumber>" — stable per billing event.
 */
function createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, printSetting, tableType }) {
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `bill:${branchOrderNumber}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const isExclusiveGST = printSetting?.exclusiveGST ?? false;
  const roundOffType = printSetting?.lowerRoundOff
    ? "lowerRoundOff"
    : printSetting?.upperRoundOff
      ? "upperRoundOff"
      : "standardRoundOff";

  // Calculate totals (same as ProductTable in BillComponent)
  const subTotal = parseFloat(printData?.itemsTotal || 0);
  const discount = parseFloat(printData?.discount || 0);
  const otherCharges = parseFloat(printData?.otherCharges || 0);
  const tablePrice = parseFloat(printData?.tablePrice || 0);
  const gstCharges = isExclusiveGST
    ? parseFloat(printData?.cgstCharges || 0) + parseFloat(printData?.sgstCharges || 0)
    : 0;
  const unrounded = subTotal - discount + otherCharges + tablePrice + gstCharges;
  const grandTotal = roundOffTotal(unrounded, roundOffType);
  const roundOff = grandTotal - unrounded;

  const lines = [];

  // ── Header ──────────────────────────────────────────────────────────────────
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true, size: 28 });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  if (fssaiNumber) lines.push({ text: `FSSAI: ${fssaiNumber}`, align: "center" });
  lines.push({ text: "--------------------------------", align: "center" });

  // ── Order meta ───────────────────────────────────────────────────────────────
  const { branchOrderNumber: bon, orderNumber, customerName, customerMobile, orderType, tableNumber, paymentType, updatedAt } = printData;
  if (bon) lines.push({ columns: ["Bill No", bon] });
  if (orderNumber) lines.push({ columns: ["Order No", String(orderNumber)] });
  if (updatedAt) {
    const d = new Date(updatedAt);
    lines.push({ columns: ["Date", isNaN(d) ? updatedAt : d.toLocaleString("en-IN")] });
  }
  if (orderType) lines.push({ columns: ["Type", orderType] });
  if (tableNumber) lines.push({ columns: ["Table", String(tableNumber)] });
  if (paymentType) lines.push({ columns: ["Payment", paymentType] });
  if (printSetting?.enableCustomer && customerName) {
    lines.push({ columns: ["Name", `${customerName}${customerMobile ? ` (${customerMobile})` : ""}`] });
  }
  lines.push({ text: "--------------------------------", align: "center" });

  // ── Items ────────────────────────────────────────────────────────────────────
  lines.push({ columns: ["Item  Qty  Rate", "Amt"], bold: true });
  lines.push({ text: "--------------------------------", align: "center" });

  (printData?.orderItems ?? [])
    .filter((item) => item.quantity > 0)
    .forEach((item) => {
      const unitPrice = price(tableType?.tableType, item) || item.itemPrice || 0;
      const lineTotal = (item.quantity * unitPrice).toFixed(2);
      lines.push({ columns: [`${item.itemName}`, lineTotal] });
      lines.push({ text: `  ${item.quantity} x ₹${unitPrice}` });
    });

  lines.push({ text: "--------------------------------", align: "center" });

  // ── Totals ───────────────────────────────────────────────────────────────────
  if (discount !== 0 || gstCharges !== 0 || otherCharges !== 0 || tablePrice !== 0) {
    lines.push({ columns: ["Sub Total", `₹${subTotal.toFixed(2)}`] });
  }
  if (discount !== 0) lines.push({ columns: ["Discount(-)", `₹${discount.toFixed(2)}`] });
  if (isExclusiveGST && gstCharges !== 0) lines.push({ columns: ["GST(+)", `₹${gstCharges.toFixed(2)}`] });
  if (tablePrice !== 0) lines.push({ columns: ["Table Price(+)", `₹${tablePrice.toFixed(2)}`] });
  if (otherCharges !== 0) lines.push({ columns: ["Charges(+)", `₹${otherCharges.toFixed(2)}`] });
  if (parseFloat(roundOff.toFixed(2)) !== 0) lines.push({ columns: ["Round Off", `${roundOff > 0 ? "+" : ""}${roundOff.toFixed(2)}`] });
  lines.push({ columns: ["GRAND TOTAL", `₹${grandTotal.toFixed(2)}`], bold: true });

  // ── GST summary ──────────────────────────────────────────────────────────────
  if (gstNumber) {
    lines.push({ text: "--------------------------------", align: "center" });
    lines.push({ text: "Tax Summary", align: "center" });
    lines.push({ text: `GST ${gstNumber}`, align: "center" });
  }

  // ── Footer ───────────────────────────────────────────────────────────────────
  if (receiptMessage) {
    lines.push({ text: "--------------------------------", align: "center" });
    lines.push({ text: receiptMessage, align: "center" });
  }

  return { jobId, printer: "bill", lines };
}

const PrintComponent = ({
  headers,
  footers,
  printData,
  printType,
  restaurant,
  logo,
  gstNumber,
  fssaiNumber,
  branchAddress,
  containerStyle,
  receiptMessage,
  printSetting,
  isRestaurantAdmin,
  tableType,
}) => {
  const isPrinting = useDetectPrint();

  const componentRef = useRef();
  const androidJobs = useRef(new Set());
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isRestaurantAdmin) {
      handlePrint();
      return;
    }

    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid() && printType !== "cashbook") {
      try {
        const receipt = createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, printSetting, tableType });
        if (androidJobs.current.has(receipt.jobId)) return; // deduplicate within this mount
        androidJobs.current.add(receipt.jobId);
        androidPrinter.print(receipt).then(
          (result) => console.log("Android bill print:", result.status, receipt.jobId),
          (error) => {
            console.error("Android bill print failed:", error.message, error.result);
            window.alert(`Print failed: ${error.message}`);
          }
        );
      } catch (error) {
        console.error("Android bill receipt build error:", error.message);
        window.alert(`Print error: ${error.message}`);
      }
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  const renderComponent = () => {
    console.log(
      "cash book",
      printData,
      restaurant,
      logo,
      gstNumber,
      receiptMessage,
      branchAddress,
      printSetting,
    );
    if (printType === "cashbook") {
      return (
        <CashBookPrint
          ref={componentRef}
          printData={printData}
          restaurant={restaurant}
          logo={logo}
          gstNumber={gstNumber}
          fssaiNumber={fssaiNumber}
          receiptMessage={receiptMessage}
          branchAddress={branchAddress}
          printSetting={printSetting}
        />
      );
    }
    return (
      <BillComponent
        ref={componentRef}
        orderData={printData}
        restaurant={restaurant}
        logo={logo}
        gstNumber={gstNumber}
        fssaiNumber={fssaiNumber}
        receiptMessage={receiptMessage}
        branchAddress={branchAddress}
        printSetting={printSetting}
        tableType={tableType}
      />
    );
  };
  return (
    <div style={{ ...containerStyle, width: "100%" }}>
      <div style={{ display: "none" }}>{renderComponent()}</div>
    </div>
  );
};
export default PrintComponent;
